import * as CGI from '@/api/operator/cgi/smb/personalCenter';
import { reportUserBehaviorCgi } from '@/api/operator/cgi/smb/common';
import dayjs from 'dayjs';
import { dailyShowCheck, WarnDialogConfig } from '@/common-components/warn-dialog';
import { User } from '@/model/user';
import globalStore from '@/stores';
import commonReport from '@/common/common-report';

// 修改微信号弹窗
export async function getEditWechatNumberDialogConfig(): Promise<WarnDialogConfig | null> {
  const account = globalStore.account;
  const needUpdateWechatNumber = Boolean(account.need_update_wechat_number);

  // 修改微信号弹窗
  if (needUpdateWechatNumber) {
    const reportUserBehavior = (behavior: string) => {
      const data = {
        behavior,
        page1: '概览',
        page2: '个人中心弹窗',
        page3: '',
      };
      reportUserBehaviorCgi({ data });
    };
    const config: WarnDialogConfig = {
      viewConfig: {
        saveKey: 'update_wechat_number',
        saveValue: dayjs().format('YYYY-MM-DD'),
        showCheck: dailyShowCheck,
        showCallback: () => {
          reportUserBehavior('修改微信号弹窗-展示');
          commonReport.reportAction('update-wechat-number-warn.show');
        },
        showNextAfterClose: false,
      },
      closeButton: true,
      cancelButton: false,
      handleSubmit: () => {
        reportUserBehavior('修改微信号弹窗-点击');
        globalStore.history.push('/agency/manage/modify-wechat-number');
      },
      title: '信息提示',
      submitLabel: '去修改微信号',
      content: <span>用户您好，系统监测到您的微信号与服务商系统的预留微信号不一致，请您及时进行修改，以免影响您的使用。</span>,
      submitButtonProps: {
        'data-report': 'update-wechat-number-warn.submit',
      },
    };
    return config;
  }
  return null;
}

// 获取手机号修改弹窗的配置
export async function getPhoneEditDialogConfig(): Promise<WarnDialogConfig | null> {
  const reportUserBehavior = (item: WarnDialogConfig) => {
    const data = {
      behavior: item.submitLabel,
      page1: '概览',
      page2: '个人中心弹窗',
      page3: '',
    };
    reportUserBehaviorCgi({ data });
  };
  const handleSubmit = (item: WarnDialogConfig) => {
    reportUserBehavior(item);
    globalStore.history.push('/agency/personal-center/index?show=true');
  };

  const phoneShowType = await getPhoneEditShowType(globalStore.account);
  const todayTimeStr = dayjs().format('YYYY-MM-DD');
  switch (phoneShowType) {
    case PhoneEditShowType.NotHaveMobile:
      return {
        viewConfig: {
          saveKey: 'phone_not_have_mobile',
          saveValue: todayTimeStr,
          // 没有手机号的用户登录一次提醒一次，但旧实现有Bug，每打开一个tab就提示一次，跨团队沟通问题，先保留此Bug
          useSessionStorage: true,
          showNextAfterClose: false,
          showCallback: () => {
            commonReport.reportAction('phone-not-have-mobile-warn.show');
          },
        },
        closeButton: true,
        cancelButton: false,
        handleSubmit,
        title: '信息提示',
        submitLabel: '去登记手机号',
        content: <span>用户您好，您尚未在本系统登记手机号，请点击“去登记手机号”进入“个人中心”补充手机号，否则可能会影响系统使用。填写手机号后本系统可拉取到您在腾讯营销学堂的学习信息，以便为您精准推荐学习资源。</span>,
        submitButtonProps: {
          'data-report': 'phone-not-have-mobile-warn.submit',
        },
      };
    case PhoneEditShowType.HaveMobileChange:
      return {
        viewConfig: {
          saveKey: 'phone_have_mobile_change',
          saveValue: todayTimeStr,
          showCheck: dailyShowCheck,
          showNextAfterClose: false,
          showCallback: () => {
            commonReport.reportAction('phone-have-mobile-change-warn.show');
          },
        },
        closeButton: true,
        cancelButton: false,
        title: '信息提示',
        submitLabel: '我知道了',
        content: (
          <span>
            用户您好，您在本系统登记的手机号与您营销学堂帐号中的手机号不一致，请进入“个人中心”或前往腾讯营销学堂（
            <a href={'https://edu.tencentads.com'} target="_blank" rel="noreferrer" data-report="phone-have-mobile-change-warn.to-edu">
              edu.tencentads.com
            </a>
            ）修改，保持手机号一致后本系统可拉取到您在腾讯营销学堂的学习信息，以便为您精准推荐学习资源。
          </span>
        ),
        submitButtonProps: {
          'data-report': 'phone-have-mobile-change-warn.submit',
        },
        closeButtonProps: {
          'data-report': 'phone-have-mobile-change-warn.close',
        },
      };
    case PhoneEditShowType.HaveMobileNotChange:
      return {
        viewConfig: {
          saveKey: 'phone_have_mobile_not_change',
          saveValue: todayTimeStr,
          showCheck: dailyShowCheck,
          showNextAfterClose: false,
          showCallback: () => {
            commonReport.reportAction('phone-have-mobile-not-change.show');
          },
        },
        closeButton: true,
        cancelButton: false,
        handleSubmit,
        title: '信息提示',
        submitLabel: '去修改手机号',
        content: (
          <span>
            用户您好，您在本系统登记的手机号与您营销学堂帐号中的手机号不一致，请点击“去修改手机号”或前往腾讯营销学堂（
            <a href={'https://edu.tencentads.com'} target="_blank" rel="noreferrer"  data-report="phone-have-mobile-not-change.to-edu">
              edu.tencentads.com
            </a>
            ）修改。保持两端手机号一致后本系统可拉取到您在腾讯营销学堂的学习信息，以便为您精准推荐学习资源。
          </span>
        ),
        submitButtonProps: {
          'data-report': 'phone-have-mobile-not-change.submit',
        },
      };
    case PhoneEditShowType.None:
    default:
      return null;
  }
}

enum PhoneEditShowType {
  None,
  HaveMobileNotChange,
  HaveMobileChange,
  NotHaveMobile,
}

// 获取修改手机号弹窗提示的类型
async function getPhoneEditShowType(account: User): Promise<PhoneEditShowType> {
  const { user_id, company_id } = account;
  const releaseTime = 1607518560000; // 在“个人中心”上线后，没有修改过手机号（即last_mod_time<2020/12/9  8:56:00 PM）
  const data = {
    user_id,
    company_id,
  };
  return CGI.getUserBaseInfo({ data }).then((res) => {
    if (res.code !== 0) {
      return PhoneEditShowType.None;
    }
    if (res.data.mobile) {
      if (res.data.mobile_match_status !== '已匹配') {
        if (res.data.last_mod_time && new Date(res.data.last_mod_time).getTime() < releaseTime) {
          return PhoneEditShowType.HaveMobileNotChange;
        }
        return PhoneEditShowType.HaveMobileChange;
      }
    } else {
      return PhoneEditShowType.NotHaveMobile;
    }
    return PhoneEditShowType.None;
  }, () => PhoneEditShowType.None);
}
